import { memo, FC, ReactNode, useCallback } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import styled from 'styled-components';

import { APP_ROUTES, AUTH_ROUTES } from '@navigation/types';
import { cookieService } from '@services/cookie.service';
import { theme } from 'theme';
import { AlertProvider } from '@providers/AlertProvider';
import { useAppDispatch } from '@redux/store';
import { authSlice } from '@redux/state/authSlice';
import { useAuthUser } from '@redux/selectors/auth';
import { clearRedirectPath } from '@utils/redirect';
import {
  HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
  PRIVACY_POLICY_URL,
  TERMS_OR_SERVICE_URL,
} from '@utils/constants';
import { DefaultText, PremierText } from '@components/text';
import SkewedButton from '@components/buttons/SkewedButton';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import backgroundImage from '@assets/images/auth.jpg';
import logo from '@assets/images/omili-logo.png';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => `${theme.colors.black}${theme.opacity[50]}`};
  z-index: 1;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Header = styled.div`
  width: 100%;
  min-height: ${HEADER_HEIGHT}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-height: ${MOBILE_HEADER_HEIGHT}px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  cursor: default;
  user-select: none;
`;

const Logo = styled(TouchableOpacity)`
  max-width: 72px;
  max-height: 72px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 60px;
    max-height: 60px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const GuestButton = styled(TouchableOpacity)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 4px 8px;
  }
`;

const GuestText = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xl};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fonts.fontSize.lg};
  }
`;

const StyledSkewed = styled(SkewedButton)`
  padding: 4px 24px;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 4px 8px;
  }
`;

const ButtonText = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xl};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fonts.fontSize.base};
  }
`;

const Body = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  padding: 16px;
  gap: 32px;
`;

const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const LinkText = styled(PremierText)`
  color: ${({ theme }) => theme.colors.grey[300]};
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
`;

const FooterText = styled(DefaultText)`
  color: ${({ theme }) => theme.colors.grey[300]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
`;

type Props = {
  children?: ReactNode;
};

const AuthLayout: FC<Props> = ({ children }) => {
  const { user: authUser } = useAuthUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const checkAuth = useCallback(() => {
    const token = cookieService.getAuthToken();
    if (token || authUser?.id) {
      cookieService.removeAuthToken();
    }
    dispatch(authSlice.actions.setUser(null));
  }, [dispatch, authUser?.id]);

  const onEnterAsGuest = useCallback(() => {
    checkAuth();
    clearRedirectPath();
    navigate(APP_ROUTES.HOME, { replace: true });
  }, [checkAuth, navigate]);

  const onLogin = useCallback(() => {
    checkAuth();
    navigate(AUTH_ROUTES.LOGIN, { replace: true });
  }, [checkAuth, navigate]);

  const onSignUp = useCallback(() => {
    checkAuth();
    navigate(AUTH_ROUTES.SIGN_UP, { replace: true });
  }, [checkAuth, navigate]);

  return (
    <AlertProvider>
      <Container>
        <Overlay />

        <Content>
          <Header>
            <LogoContainer>
              <Logo disabled>
                <Image src={logo} alt='logo' />
              </Logo>
            </LogoContainer>

            <ButtonsContainer>
              <GuestButton onClick={onEnterAsGuest}>
                <GuestText>Enter as a guest</GuestText>
              </GuestButton>

              <StyledSkewed onClick={onLogin} isBorder>
                <ButtonText>Log In</ButtonText>
              </StyledSkewed>

              <StyledSkewed onClick={onSignUp}>
                <ButtonText>Sign Up</ButtonText>
              </StyledSkewed>
            </ButtonsContainer>
          </Header>

          <Body>
            {children || <Outlet />}

            <Footer>
              <LinksContainer>
                <StyledLink
                  to={TERMS_OR_SERVICE_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <LinkText>Terms of Service</LinkText>
                </StyledLink>

                <StyledLink
                  to={PRIVACY_POLICY_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <LinkText>Privacy Policy</LinkText>
                </StyledLink>
              </LinksContainer>

              <FooterText>© 2025 Omili</FooterText>
            </Footer>
          </Body>
        </Content>
      </Container>
    </AlertProvider>
  );
};

export default memo(AuthLayout);
